import { Drawer, Layout } from "antd"
import React, { useCallback } from "react"
import { useNavigate } from "react-router"

import { useAppDispatch } from "../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { toggleMobileMenu } from "../../redux/nav/nav.slice.ts"
import { markProjectNotificationsSeen } from "../../redux/notificationCounts/notification-counts.thunks.ts"
import { SidebarBody } from "./SidebarBody.tsx"
import {
  approvalItemDeepLink,
  eventDeepLink,
  projectDeepLink
} from "./helpers/notification-deep-links.ts"
import { type SidebarItem } from "./sidebar-config.tsx"
import "./styles.less"

const backgroundColor = "#FFFFFF"

// Kept in step with `--sidebar-width` / `--sidebar-width-collapsed` in
// tailwind.css, which the header's brand zone lines itself up against.
export const SIDEBAR_WIDTH = 236
export const SIDEBAR_COLLAPSED_WIDTH = 64

export const SidebarController = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const isMobile = useAppSelector((state) => state?.nav.isMobile)
  const isMobileMenuOpen = useAppSelector(
    (state) => state?.nav.isMobileMenuOpen
  )
  const isSidebarOpen = useAppSelector((state) => state?.nav.isSidebarOpen)

  // Every one of these degrades to the row's normal route when there is
  // nothing new/pending — projectDeepLink([]) etc. already fall back to the
  // module's own page, so a nav row with no badge behaves exactly as before.
  const projectItems = useAppSelector(
    (state) => state.notificationCounts.projectItems
  )
  const calendarItems = useAppSelector(
    (state) => state.notificationCounts.calendarItems
  )
  const approvals = useAppSelector((state) => state.notificationCounts.approvals)

  const handleToggleMobileMenu = useCallback(() => {
    dispatch(toggleMobileMenu())
  }, [dispatch])

  const handleMenuClick = useCallback(
    (item: Pick<SidebarItem, "key">) => {
      switch (item?.key) {
        case "projects":
          // The single-new-project deep link opens `/projects/{id}` directly,
          // which ProjectsViewResolver (the grid/list route) never mounts on —
          // so that path has no other chance to clear the badge it was
          // clicked from. Marking here is a no-op when nothing is unread
          // (see notification-counts.thunks.test.ts), and ProjectsViewResolver
          // still marks on its own mount for direct/bookmarked visits.
          dispatch(markProjectNotificationsSeen())
          navigate(projectDeepLink(projectItems))
          return
        case "calendar":
          navigate(eventDeepLink(calendarItems))
          return
        case "proposals":
          navigate(approvalItemDeepLink("proposal", approvals.proposalItems))
          return
        case "contracts":
          navigate(approvalItemDeepLink("contract", approvals.contractItems))
          return
        default:
          navigate(`/${item?.key}`)
      }
    },
    [dispatch, navigate, projectItems, calendarItems, approvals]
  )

  const handleMobileMenuClick = useCallback(
    (item: Pick<SidebarItem, "key">) => {
      handleMenuClick(item)
      handleToggleMobileMenu()
    },
    [handleMenuClick, handleToggleMobileMenu]
  )

  if (isMobile) {
    return (
      <Drawer
        title={null}
        placement="left"
        onClose={handleToggleMobileMenu}
        open={isMobileMenuOpen}
        width={280}
        className="md:hidden"
        styles={{
          body: { padding: 0, backgroundColor },
          header: { display: "none" }
        }}
      >
        <div className={"md:px-4 size-full"}>
          <SidebarBody onItemSelect={handleMobileMenuClick} />
        </div>
      </Drawer>
    )
  }

  // The collapse control lives beside the logo in the header now, so the Sider
  // carries no trigger of its own.
  return (
    <Layout.Sider
      collapsedWidth={SIDEBAR_COLLAPSED_WIDTH}
      collapsed={!isSidebarOpen}
      collapsible
      trigger={null}
      className="ezb-sidebar hidden group z-10 sticky top-[var(--header-height)] h-[calc(100svh-var(--header-height))] border-r border-[#E2E8F0] md:block"
      data-state={isSidebarOpen ? "open" : "closed"}
      width={SIDEBAR_WIDTH}
      style={{ backgroundColor }}
    >
      <SidebarBody onItemSelect={handleMenuClick} />
    </Layout.Sider>
  )
}
